import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface Strain {
  id?: number;
  name: string;
  type?: string;
  thc_min?: number;
  thc_max?: number;
  cbd_min?: number;
  cbd_max?: number;
  effects?: string[];
  flavors?: string[];
  medical_uses?: string[];
  description?: string;
  image_url?: string;
}

/* ── Helpers ── */
function typeBadgeColor(type?: string | null | any) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'sativa': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'indica': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'hybrid': return 'bg-green-500/20 text-green-300 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

function avgThc(s: Strain): number {
  const min = s.thc_min ?? 0;
  const max = s.thc_max ?? min;
  return (min + max) / 2;
}

function avgCbd(s: Strain): number {
  const min = s.cbd_min ?? 0;
  const max = s.cbd_max ?? min;
  return (min + max) / 2;
}

function formatRange(min?: number, max?: number): string {
  if (min == null && max == null) return 'N/A';
  if (min != null && max != null && min !== max) return `${min}% - ${max}%`;
  return `${min ?? max}%`;
}

/* ── Versus Bar ── */
function VersusBar({ label, valueA, valueB, maxVal }: { label: string; valueA: number; valueB: number; maxVal: number }) {
  const clamp = maxVal > 0 ? maxVal : 1;
  const pctA = Math.min((valueA / clamp) * 100, 100);
  const pctB = Math.min((valueB / clamp) * 100, 100);

  return (
    <div className="space-y-1">
      <div className="text-xs text-white/50 text-center font-medium uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-white/70 w-12 text-right">{valueA.toFixed(1)}%</span>
        <div className="flex-1 flex items-center gap-1">
          <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden flex justify-end">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pctA}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="h-full rounded-full bg-[#4ade80]"
            />
          </div>
          <div className="w-px h-5 bg-white/20" />
          <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pctB}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="h-full rounded-full bg-[#d4a574]"
            />
          </div>
        </div>
        <span className="text-xs text-white/70 w-12">{valueB.toFixed(1)}%</span>
      </div>
    </div>
  );
}

/* ── Tag Lists (shared / unique) ── */
function TagComparison({ label, listA, listB }: { label: string; listA: string[]; listB: string[] }) {
  const safeStr = (s: any) => typeof s === 'string' ? s : String(s);
  const setA = new Set(listA.map(s => safeStr(s).toLowerCase()));
  const setB = new Set(listB.map(s => safeStr(s).toLowerCase()));
  const shared = listA.filter(s => setB.has(safeStr(s).toLowerCase()));
  const uniqueA = listA.filter(s => !setB.has(safeStr(s).toLowerCase()));
  const uniqueB = listB.filter(s => !setA.has(safeStr(s).toLowerCase()));

  if (listA.length === 0 && listB.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wider text-center">{label}</h4>

      {shared.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-white/40 text-center">Shared</p>
          <div className="flex flex-wrap justify-center gap-1.5">
            {shared.map(t => (
              <span key={t} className="px-2.5 py-1 text-xs rounded-full bg-white/10 text-white/80 border border-white/10">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          {uniqueA.length > 0 && (
            <>
              <p className="text-xs text-[#4ade80]/60 text-center">Only Strain A</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {uniqueA.map(t => (
                  <span key={t} className="px-2.5 py-1 text-xs rounded-full bg-[#4ade80]/10 text-[#4ade80] border border-[#4ade80]/20">
                    {t}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="space-y-1">
          {uniqueB.length > 0 && (
            <>
              <p className="text-xs text-[#d4a574]/60 text-center">Only Strain B</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {uniqueB.map(t => (
                  <span key={t} className="px-2.5 py-1 text-xs rounded-full bg-[#d4a574]/10 text-[#d4a574] border border-[#d4a574]/20">
                    {t}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Autocomplete Picker ── */
function StrainPicker({
  label,
  strains,
  selected,
  onSelect,
  accentColor,
}: {
  label: string;
  strains: Strain[];
  selected: Strain | null;
  onSelect: (s: Strain | null) => void;
  accentColor: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const qStr = typeof query === 'string' ? query : '';
    if (!qStr.trim()) return strains.slice(0, 30);
    const q = qStr.toLowerCase();
    return strains.filter(s => typeof s.name === 'string' && s.name.toLowerCase().includes(q)).slice(0, 30);
  }, [query, strains]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative flex-1">
      <label className="block text-xs font-medium text-white/40 mb-1 uppercase tracking-wider">{label}</label>
      {selected ? (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border bg-white/[0.02]`} style={{ borderColor: `${accentColor}33` }}>
          <span className="text-sm text-white flex-1 truncate">{selected.name}</span>
          <button
            onClick={() => { onSelect(null); setQuery(''); }}
            className="text-white/40 hover:text-white/70 text-lg leading-none"
          >
            x
          </button>
        </div>
      ) : (
        <input
          type="text"
          placeholder="Search strains..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="w-full px-3 py-2 rounded-xl border border-white/10 bg-white/[0.02] text-sm text-white placeholder-white/30 outline-none focus:border-white/20 transition-colors"
        />
      )}
      <AnimatePresence>
        {open && !selected && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-cannabis-900 shadow-xl"
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-white/30">No strains found</div>
            ) : (
              filtered.map(s => (
                <button
                  key={s.id ?? s.name}
                  onClick={() => { onSelect(s); setOpen(false); setQuery(''); }}
                  className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5 transition-colors flex items-center gap-2"
                >
                  <span className="flex-1 truncate">{s.name}</span>
                  {s.type && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${typeBadgeColor(s.type)}`}>
                      {s.type}
                    </span>
                  )}
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main Component ── */
export default function StrainCompareTab(_props: CountryProps) {
  const [allStrains, setAllStrains] = useState<Strain[]>([]);
  const [loading, setLoading] = useState(true);
  const [strainA, setStrainA] = useState<Strain | null>(null);
  const [strainB, setStrainB] = useState<Strain | null>(null);

  useEffect(() => {
    async function fetchStrains() {
      try {
        const res = await fetch(getApiUrl('/api/strains-data?per_page=500'));
        if (!res.ok) throw new Error('Failed to fetch strains');
        const data = await res.json();
        const items: Strain[] = data.items ?? data.data ?? data;
        setAllStrains(Array.isArray(items) ? items : []);
      } catch {
        setAllStrains([]);
      } finally {
        setLoading(false);
      }
    }
    fetchStrains();
  }, []);

  const handleSwap = () => {
    setStrainA(strainB);
    setStrainB(strainA);
  };

  const handleClear = () => {
    setStrainA(null);
    setStrainB(null);
  };

  const bothSelected = strainA && strainB;
  const maxThc = Math.max(avgThc(strainA ?? {} as Strain), avgThc(strainB ?? {} as Strain), 30);
  const maxCbd = Math.max(avgCbd(strainA ?? {} as Strain), avgCbd(strainB ?? {} as Strain), 5);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 animate-pulse bg-white/5 rounded-lg" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-12 animate-pulse bg-white/5 rounded-xl" />
          <div className="h-12 animate-pulse bg-white/5 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      {/* Selection Row */}
      <motion.div variants={staggerItem} className="space-y-3">
        <div className="flex items-end gap-3">
          <StrainPicker
            label="Strain A"
            strains={allStrains}
            selected={strainA}
            onSelect={setStrainA}
            accentColor="#4ade80"
          />

          <div className="flex flex-col items-center gap-1 pb-1">
            <button
              onClick={handleSwap}
              disabled={!strainA && !strainB}
              className="p-2 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Swap strains"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </button>
          </div>

          <StrainPicker
            label="Strain B"
            strains={allStrains}
            selected={strainB}
            onSelect={setStrainB}
            accentColor="#d4a574"
          />
        </div>

        {(strainA || strainB) && (
          <div className="flex justify-center">
            <button
              onClick={handleClear}
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
            >
              Clear comparison
            </button>
          </div>
        )}
      </motion.div>

      {/* Comparison View */}
      <AnimatePresence mode="wait">
        {bothSelected && (
          <motion.div
            key={`${strainA.name}-${strainB.name}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="flex items-center justify-center gap-4">
              <h2 className="text-lg font-bold text-[#4ade80] truncate max-w-[40%] text-right">{strainA.name}</h2>
              <span className="text-xs font-bold text-white/30 uppercase tracking-widest">VS</span>
              <h2 className="text-lg font-bold text-[#d4a574] truncate max-w-[40%] text-left">{strainB.name}</h2>
            </div>

            {/* Images + Type */}
            <div className="grid grid-cols-2 gap-4">
              {[strainA, strainB].map((s, i) => (
                <motion.div
                  key={s.name}
                  variants={staggerItem}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
                >
                  {s.image_url ? (
                    <img src={s.image_url} alt={s.name} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-white/5 flex items-center justify-center">
                      <span className="text-white/20 text-sm">No image</span>
                    </div>
                  )}
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-white truncate">{s.name}</span>
                    {s.type && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadgeColor(s.type)}`}>
                        {s.type}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* THC / CBD Bars */}
            <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
              <VersusBar label="THC" valueA={avgThc(strainA)} valueB={avgThc(strainB)} maxVal={maxThc} />
              <div className="flex items-center justify-between text-[10px] text-white/30">
                <span>{formatRange(strainA.thc_min, strainA.thc_max)}</span>
                <span>{formatRange(strainB.thc_min, strainB.thc_max)}</span>
              </div>
              <div className="border-t border-white/5" />
              <VersusBar label="CBD" valueA={avgCbd(strainA)} valueB={avgCbd(strainB)} maxVal={maxCbd} />
              <div className="flex items-center justify-between text-[10px] text-white/30">
                <span>{formatRange(strainA.cbd_min, strainA.cbd_max)}</span>
                <span>{formatRange(strainB.cbd_min, strainB.cbd_max)}</span>
              </div>
            </motion.div>

            {/* Effects */}
            <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <TagComparison
                label="Effects"
                listA={strainA.effects ?? []}
                listB={strainB.effects ?? []}
              />
            </motion.div>

            {/* Flavors */}
            <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <TagComparison
                label="Flavors"
                listA={strainA.flavors ?? []}
                listB={strainB.flavors ?? []}
              />
            </motion.div>

            {/* Medical Uses */}
            <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <TagComparison
                label="Medical Uses"
                listA={strainA.medical_uses ?? []}
                listB={strainB.medical_uses ?? []}
              />
            </motion.div>

            {/* Descriptions */}
            <motion.div variants={staggerItem} className="grid grid-cols-2 gap-4">
              {[strainA, strainB].map((s, i) => (
                <div
                  key={s.name}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-2"
                >
                  <h4 className={`text-sm font-semibold ${i === 0 ? 'text-[#4ade80]' : 'text-[#d4a574]'}`}>
                    {s.name}
                  </h4>
                  <p className="text-xs text-white/60 leading-relaxed">
                    {s.description || 'No description available.'}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!bothSelected && !loading && (
        <motion.div
          variants={staggerItem}
          className="text-center py-16 space-y-3"
        >
          <div className="text-white/10 text-6xl">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-white/30 text-sm">Select two strains above to compare them side by side</p>
          <p className="text-white/20 text-xs">{allStrains.length} strains available</p>
        </motion.div>
      )}
    </motion.div>
  );
}
